import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRoReportingGroups } from "../../api/roReporting";
import {
    getMyDeliveryShareStatistics,
    getTodayDeliveriesStatistics,
    getSessionStatistics,
    putMyDeliveryShareGoals,
    type UserDeliveryShareStatistics,
    type UpdateUserDeliveryShareGoalsRequest
} from "../../api/deliveries";
import { useTelemetry } from "../../shared/useTelemetry";
import { getCurrentSessionId } from "../utils/sessionManager";
import { useCurrentUser } from "../../api/auth";
import { electionDayDailyTarget } from "../../shared/electionDayGoals";
import "./MapStatusBar.css";

interface MapStatusBarProps {
    campaignId?: string;
    onShowRoReporting?: () => void;
    onShowMissedAddresses?: () => void;
}

const qtySv = (n: number) => new Intl.NumberFormat("sv-SE").format(n);

function GoalsSummary({ stats }: { stats: UserDeliveryShareStatistics }): JSX.Element | null {
    const parts: string[] = [];
    if (stats.weekGoalQuantity != null) {
        const pct = Math.round(Math.min(999, (stats.calendarWeekTotalQuantity / Math.max(1, stats.weekGoalQuantity)) * 100));
        parts.push(`V ${pct}%`);
    }
    if (stats.monthGoalQuantity != null) {
        const pct = Math.round(Math.min(999, (stats.calendarMonthTotalQuantity / Math.max(1, stats.monthGoalQuantity)) * 100));
        parts.push(`M ${pct}%`);
    }
    if (stats.yearGoalQuantity != null) {
        const pct = Math.round(Math.min(999, (stats.calendarYearTotalQuantity / Math.max(1, stats.yearGoalQuantity)) * 100));
        parts.push(`13/9 ${pct}%`);
    }
    if (parts.length === 0) return null;
    return <span className="map-status-bar__compact-goals">{parts.join(" · ")}</span>;
}

function hasAnyGoalSet(s: UserDeliveryShareStatistics): boolean {
    return (
        s.weekGoalQuantity != null || s.monthGoalQuantity != null || s.yearGoalQuantity != null
    );
}

type GoalVariant = "day" | "week" | "month" | "year";

function GoalMiniBar({
    label,
    shortLabel,
    current,
    goal,
    variant
}: {
    label: string;
    shortLabel: string;
    current: number;
    goal: number;
    variant: GoalVariant;
}): JSX.Element {
    const safeGoal = Math.max(goal, 1);
    const pctRaw = (current / safeGoal) * 100;
    const pctBar = Math.min(100, pctRaw);
    const pctLabel = Math.min(999, Math.round(pctRaw));
    const complete = pctRaw >= 100;

    return (
        <div
            className={`map-goal-mini map-goal-mini--${variant}${complete ? " map-goal-mini--complete" : ""}`}
            role="group"
            aria-label={`${label}: ${pctLabel} procent, ${qtySv(current)} av ${qtySv(goal)} flygblad`}
        >
            <div className="map-goal-mini__head">
                <span className="map-goal-mini__label">
                    <abbr className="map-goal-mini__abbr" title={label}>
                        {shortLabel}
                    </abbr>
                    <span className="map-goal-mini__label-full">{label}</span>
                </span>
                <span className="map-goal-mini__pct">{pctLabel}%</span>
            </div>
            <div className="map-goal-mini__track">
                <div
                    className="map-goal-mini__fill"
                    style={{ width: `${pctBar}%` }}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(pctBar)}
                />
            </div>
            <div className="map-goal-mini__nums">
                {qtySv(current)} / {qtySv(goal)}
            </div>
        </div>
    );
}

function MapGoalsBars({ stats }: { stats: UserDeliveryShareStatistics }): JSX.Element | null {
    const { t } = useTranslation();
    const electionPace = useMemo(() => {
        if (stats.yearGoalQuantity == null) {
            return null;
        }
        return electionDayDailyTarget(stats.yearGoalQuantity, stats.calendarYearTotalQuantity, new Date());
    }, [stats.yearGoalQuantity, stats.calendarYearTotalQuantity]);

    const items: JSX.Element[] = [];
    if (electionPace) {
        items.push(
            <GoalMiniBar
                key="d"
                label={t("stats.goalTodayPace")}
                shortLabel="D"
                variant="day"
                current={stats.todayTotalQuantity}
                goal={electionPace.dailyTarget}
            />
        );
    }
    if (stats.weekGoalQuantity != null) {
        items.push(
            <GoalMiniBar
                key="w"
                label="Vecka"
                shortLabel="V"
                variant="week"
                current={stats.calendarWeekTotalQuantity}
                goal={stats.weekGoalQuantity}
            />
        );
    }
    if (stats.monthGoalQuantity != null) {
        items.push(
            <GoalMiniBar
                key="m"
                label="Månad"
                shortLabel="M"
                variant="month"
                current={stats.calendarMonthTotalQuantity}
                goal={stats.monthGoalQuantity}
            />
        );
    }
    if (stats.yearGoalQuantity != null) {
        items.push(
            <GoalMiniBar
                key="y"
                label={t("stats.goalElectionDay")}
                shortLabel="13/9"
                variant="year"
                current={stats.calendarYearTotalQuantity}
                goal={stats.yearGoalQuantity}
            />
        );
    }
    if (items.length === 0) {
        return null;
    }
    return <div className="map-status-bar__goals-bars">{items}</div>;
}

function parseGoalInput(raw: string): number | null | "invalid" {
    const t = raw.trim();
    if (t === "") {
        return null;
    }
    if (!/^\d+$/.test(t)) {
        return "invalid";
    }
    const n = Number(t);
    if (!Number.isSafeInteger(n) || n < 1) {
        return "invalid";
    }
    return n;
}

interface DeliveryGoalsModalProps {
    open: boolean;
    onClose: () => void;
    initial: UserDeliveryShareStatistics | undefined;
}

const DeliveryGoalsModal = ({ open, onClose, initial }: DeliveryGoalsModalProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [week, setWeek] = useState("");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState("");
    const [error, setError] = useState<string | null>(null);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) {
            return;
        }
        wasOpenRef.current = true;
        setWeek(initial?.weekGoalQuantity != null ? String(initial.weekGoalQuantity) : "");
        setMonth(initial?.monthGoalQuantity != null ? String(initial.monthGoalQuantity) : "");
        setYear(initial?.yearGoalQuantity != null ? String(initial.yearGoalQuantity) : "");
        setError(null);
    }, [open, initial]);

    const saveMutation = useMutation({
        mutationFn: (body: UpdateUserDeliveryShareGoalsRequest) => putMyDeliveryShareGoals(body),
        onSuccess: (data) => {
            queryClient.setQueryData(["deliveries", "statistics", "me"], data);
            onClose();
        },
        onError: () => {
            setError("Kunde inte spara mål. Försök igen.");
        }
    });

    if (!open) {
        return null;
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        const w = parseGoalInput(week);
        const m = parseGoalInput(month);
        const y = parseGoalInput(year);
        if (w === "invalid" || m === "invalid" || y === "invalid") {
            setError("Ange heltal ≥ 1 eller lämna fält tomt.");
            return;
        }
        const body: UpdateUserDeliveryShareGoalsRequest = {
            weekGoalQuantity: w,
            monthGoalQuantity: m,
            yearGoalQuantity: y
        };
        saveMutation.mutate(body);
    };

    return (
        <div className="map-goals-modal-backdrop" role="presentation" onClick={onClose}>
            <div
                className="map-goals-modal"
                role="dialog"
                aria-labelledby="map-goals-modal-title"
                onClick={(ev) => ev.stopPropagation()}
            >
                <h2 id="map-goals-modal-title" className="map-goals-modal__title">
                    Leveransmål
                </h2>
                <p className="map-goals-modal__hint">
                    Antal flygblad. Lämna tomt för att ta bort målet. Veckomål jämförs med summan för innevarande
                    kalendervecka (måndag–söndag, ISO-vecka) i din tidszon.
                </p>
                <form onSubmit={handleSubmit} className="map-goals-modal__form">
                    <label className="map-goals-modal__field">
                        <span>Veckomål</span>
                        <input type="text" inputMode="numeric" value={week} onChange={(e) => setWeek(e.target.value)} />
                    </label>
                    <label className="map-goals-modal__field">
                        <span>Månadsmål</span>
                        <input type="text" inputMode="numeric" value={month} onChange={(e) => setMonth(e.target.value)} />
                    </label>
                    <label className="map-goals-modal__field">
                        <span>{t("stats.goalElectionDay")}</span>
                        <input type="text" inputMode="numeric" value={year} onChange={(e) => setYear(e.target.value)} />
                    </label>
                    {error ? <p className="map-goals-modal__error">{error}</p> : null}
                    <div className="map-goals-modal__actions">
                        <button type="button" className="map-goals-modal__btn map-goals-modal__btn--secondary" onClick={onClose}>
                            Avbryt
                        </button>
                        <button type="submit" className="map-goals-modal__btn map-goals-modal__btn--primary" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? "Sparar…" : "Spara"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const MapStatusBar = ({ campaignId, onShowRoReporting, onShowMissedAddresses }: MapStatusBarProps): JSX.Element | null => {
    const telemetry = useTelemetry();
    const currentSessionId = getCurrentSessionId();
    const [goalsModalOpen, setGoalsModalOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 768px)");
        const handler = (e: MediaQueryListEvent) => { if (e.matches) setIsCollapsed(true); };
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    const userQuery = useCurrentUser({ retry: false });

    const { data: shareStats } = useQuery({
        queryKey: ["deliveries", "statistics", "me"],
        queryFn: getMyDeliveryShareStatistics,
        enabled: userQuery.isSuccess,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000
    });

    const goalsActive = shareStats ? hasAnyGoalSet(shareStats) : false;

    const { data: roGroups } = useQuery({
        queryKey: ["ro-reporting", "unreported", campaignId],
        queryFn: () =>
            getRoReportingGroups({
                campaignId: campaignId || undefined,
                isReportedToRo: false
            }),
        enabled: !!campaignId,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000
    });

    const { data: todayStats } = useQuery({
        queryKey: ["deliveries", "today", campaignId],
        queryFn: () => getTodayDeliveriesStatistics(campaignId),
        enabled: !!campaignId,
        staleTime: 10 * 1000,
        refetchInterval: 30 * 1000
    });

    const { data: sessionStats } = useQuery({
        queryKey: ["deliveries", "session", currentSessionId],
        queryFn: () => getSessionStatistics(currentSessionId),
        enabled: !!currentSessionId && !!campaignId,
        staleTime: 5 * 1000,
        refetchInterval: 10 * 1000
    });

    const unreportedCount = roGroups?.length ?? 0;
    const totalUnreportedFlyers = roGroups?.reduce((sum, group) => sum + group.totalQuantity, 0) ?? 0;
    const hasUnreported = unreportedCount > 0;

    if (!userQuery.isSuccess && !campaignId) {
        return null;
    }

    const formatStats = (deliveryCount: number, totalQuantity: number): string => {
        if (deliveryCount === totalQuantity) {
            return `${totalQuantity} flygblad`;
        }
        return `${deliveryCount} adresser, ${totalQuantity} flygblad`;
    };

    const showCampaignRow = !!campaignId;

    const campaignStatsLine = showCampaignRow && (sessionStats || todayStats)
        ? [sessionStats && `S:${sessionStats.totalQuantity}`, todayStats && `I:${todayStats.totalQuantity}`].filter(Boolean).join(" · ")
        : null;

    return (
        <>
            <DeliveryGoalsModal open={goalsModalOpen} onClose={() => setGoalsModalOpen(false)} initial={shareStats} />
            <div className={`map-status-bar${isCollapsed ? " map-status-bar--collapsed" : ""}`}>
                {/* Collapsed compact strip */}
                {isCollapsed ? (
                    <div className="map-status-bar__compact-row">
                        <div className="map-status-bar__compact-info">
                            {userQuery.isSuccess && shareStats && goalsActive ? (
                                <GoalsSummary stats={shareStats} />
                            ) : userQuery.isSuccess ? (
                                <span className="map-status-bar__compact-empty">Leveransmål</span>
                            ) : null}
                            {campaignStatsLine && (
                                <span className="map-status-bar__compact-session">{campaignStatsLine}</span>
                            )}
                            {hasUnreported && (
                                <span className="map-status-bar__compact-alert">● RO {unreportedCount}</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="map-status-bar__collapse-toggle"
                            onClick={() => setIsCollapsed(false)}
                            aria-label="Visa detaljer"
                            aria-expanded={false}
                        >
                            ▲
                        </button>
                    </div>
                ) : (
                    <div className="map-status-bar__content">
                        {/* Collapse button — visible only on mobile */}
                        <button
                            type="button"
                            className="map-status-bar__collapse-toggle map-status-bar__collapse-toggle--inline"
                            onClick={() => setIsCollapsed(true)}
                            aria-label="Dölj detaljer"
                            aria-expanded={true}
                        >
                            ▼
                        </button>

                        {userQuery.isSuccess ? (
                            <div className="map-status-bar__goals">
                                <div className="map-status-bar__goals-main">
                                    {shareStats && goalsActive ? (
                                        <MapGoalsBars stats={shareStats} />
                                    ) : (
                                        <span className="map-status-bar__goals-text map-status-bar__goals-text--muted">
                                            Inga mål ännu
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="map-status-bar__goals-button"
                                    onClick={() => {
                                        telemetry.track("deliveryGoals.openModal");
                                        setGoalsModalOpen(true);
                                    }}
                                >
                                    {goalsActive ? "Ändra mål" : "Leveransmål"}
                                </button>
                            </div>
                        ) : null}

                        {showCampaignRow ? (
                            <div className="map-status-bar__campaign-row">
                                <div className="map-status-bar__stats">
                                    {sessionStats ? (
                                        <div className="map-status-bar__stat-item">
                                            <span className="map-status-bar__stat-label">Session:</span>
                                            <span className="map-status-bar__stat-value">
                                                {formatStats(sessionStats.deliveryCount, sessionStats.totalQuantity)}
                                            </span>
                                        </div>
                                    ) : null}
                                    {todayStats ? (
                                        <div className="map-status-bar__stat-item">
                                            <span className="map-status-bar__stat-label">Idag:</span>
                                            <span className="map-status-bar__stat-value">
                                                {formatStats(todayStats.deliveryCount, todayStats.totalQuantity)}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>

                                {hasUnreported && (
                                    <div className="map-status-bar__warning">
                                        <div className="map-status-bar__warning-main">
                                            <span className="map-status-bar__warning-title">Väntar mot RO</span>
                                            <span className="map-status-bar__warning-detail">
                                                {totalUnreportedFlyers} flygblad · {unreportedCount} postnummer
                                            </span>
                                        </div>
                                        {onShowRoReporting && (
                                            <button
                                                type="button"
                                                className="map-status-bar__warning-button"
                                                aria-label={`Markera ${totalUnreportedFlyers} flygblad i ${unreportedCount} postnummer som inskickade till RO`}
                                                onClick={() => {
                                                    telemetry.track("ro-reporting.openedFromStatusBar");
                                                    onShowRoReporting();
                                                }}
                                            >
                                                <span className="map-status-bar__warning-button-label">Markera inskickade</span>
                                                <span className="map-status-bar__warning-button-count" aria-hidden="true">
                                                    {totalUnreportedFlyers}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {onShowMissedAddresses && (
                                    <div className="map-status-bar__missed">
                                        <button
                                            type="button"
                                            className="map-status-bar__missed-button"
                                            onClick={() => {
                                                telemetry.track("missedAddresses.openedFromStatusBar");
                                                onShowMissedAddresses();
                                            }}
                                        >
                                            Komplettera missade
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </>
    );
};
